/**
 * CookieConsent - GDPR-compliant cookie consent banner
 * Manages user preferences for different cookie categories
 */
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Cookie, Settings, Check } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'letwise_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'letwise_cookie_preferences';

// Cookie categories
const COOKIE_CATEGORIES = {
  necessary: {
    id: 'necessary',
    name: 'Strictly Necessary',
    description: 'Essential for the website to function. Cannot be disabled.',
    required: true,
    defaultEnabled: true
  },
  functional: {
    id: 'functional',
    name: 'Functional',
    description: 'Remember your preferences and settings across sessions.',
    required: false,
    defaultEnabled: true
  },
  analytics: {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us understand how visitors interact with our website.',
    required: false,
    defaultEnabled: false
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing',
    description: 'Used to deliver personalized advertisements.',
    required: false,
    defaultEnabled: false
  }
};

// Get stored consent
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    return consent === 'true';
  } catch {
    return false;
  }
};

// Get stored preferences
export const getCookiePreferences = () => {
  try {
    const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (prefs) {
      return JSON.parse(prefs);
    }
  } catch {
    // Ignore parsing errors
  }
  
  // Return defaults
  return {
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false
  };
};

// Check if specific cookie category is allowed
export const isCookieAllowed = (category) => {
  if (category === 'necessary') return true;
  const prefs = getCookiePreferences();
  return prefs[category] === true;
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false
  });

  // Check if consent exists on mount
  const hasExistingConsent = getCookieConsent();
  
  useEffect(() => {
    if (hasExistingConsent) {
      // Already consented - no need to show banner
      const prefs = getCookiePreferences();
      if (prefs) setPreferences(prefs);
    } else {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []); // Empty deps - only run once on mount

  const saveConsent = (prefs) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
      localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
      
      // Dispatch event for other components to react
      window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: prefs }));
    } catch {
      // localStorage might be blocked
    }
    setVisible(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptSelected = () => {
    saveConsent(preferences);
  };

  const rejectNonEssential = () => {
    const minimal = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    setPreferences(minimal);
    saveConsent(minimal);
  };

  const togglePreference = (category) => {
    if (COOKIE_CATEGORIES[category].required) return;
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!visible) return null;

  return (
    <>
      {/* Banner - no blocking backdrop for better UX */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Cookie className="w-6 h-6 text-white" />
              <h3 className="text-lg font-semibold text-white">Cookie Preferences</h3>
            </div>
            <button 
              onClick={() => setVisible(false)}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {!showSettings ? (
              // Simple view
              <div className="space-y-4">
                <p className="text-gray-600 text-sm leading-relaxed">
                  We use cookies to enhance your experience on our platform. Some cookies are essential 
                  for the website to function, while others help us improve your experience and provide 
                  personalized content. You can customize your preferences or accept all cookies.
                </p>
                <p className="text-gray-500 text-xs">
                  By clicking &quot;Accept All&quot;, you consent to our use of cookies as described in our{' '}
                  <a href="/cookies" className="text-emerald-600 hover:underline">Cookie Policy</a> and{' '}
                  <a href="/privacy" className="text-emerald-600 hover:underline">Privacy Policy</a>.
                </p>
              </div>
            ) : (
              // Settings view
              <div className="space-y-4">
                <p className="text-gray-600 text-sm mb-4">
                  Choose which cookies you want to allow. Essential cookies cannot be disabled.
                </p>
                
                <div className="space-y-3">
                  {Object.values(COOKIE_CATEGORIES).map((category) => (
                    <div 
                      key={category.id}
                      className={`flex items-start gap-4 p-3 rounded-lg border ${
                        preferences[category.id] 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <button
                        onClick={() => togglePreference(category.id)}
                        disabled={category.required}
                        className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                          preferences[category.id]
                            ? 'bg-emerald-600 text-white'
                            : 'bg-white border-2 border-gray-300'
                        } ${category.required ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-emerald-400'}`}
                        aria-label={`Toggle ${category.name}`}
                      >
                        {preferences[category.id] && <Check className="w-3 h-3" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 text-sm">{category.name}</span>
                          {category.required && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-gray-500 text-xs mt-1">{category.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-center">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Settings className="w-4 h-4" />
                {showSettings ? 'Hide settings' : 'Customize settings'}
              </button>
              
              <div className="flex flex-wrap gap-3 justify-center sm:justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rejectNonEssential}
                  className="text-gray-600"
                >
                  Reject Non-Essential
                </Button>
                
                {showSettings && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={acceptSelected}
                    className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  >
                    Save Preferences
                  </Button>
                )}
                
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Accept All Cookies
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;

// Hook for components to check cookie consent
export const useCookieConsent = () => {
  const [consent, setConsent] = useState(getCookiePreferences());

  useEffect(() => {
    const handleUpdate = (event) => {
      setConsent(event.detail);
    };

    window.addEventListener('cookieConsentUpdated', handleUpdate);
    return () => window.removeEventListener('cookieConsentUpdated', handleUpdate);
  }, []);

  return consent;
};
